/**
 * AuthDialog
 *
 * Self-contained modal with three sub-states:
 *   • Sign in (email + password + Google OAuth)
 *   • Forgot password (email submit)
 *   • Forgot success (one-time reset-link reveal in dev mode)
 *
 * Extracted from LoginPage.js to keep the landing page focused on marketing.
 * Renders nothing when `open` is false.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Envelope, Lock, GoogleLogo, ArrowRight, ArrowLeft, Copy, CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AuthDialog({ open, onClose, defaultEmail = '' }) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('signin'); // 'signin' | 'forgot' | 'forgot-success'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLink, setForgotLink] = useState(null);
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  if (!open) return null;

  const close = () => {
    setMode('signin');
    setError('');
    setForgotEmail('');
    setForgotLink(null);
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
    setLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setForgotLoading(true);
    try {
      const { data } = await authApi.forgotPassword(forgotEmail);
      setForgotLink(data.reset_link || null);
      setMode('forgot-success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to submit request. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fade-in"
      onClick={close}
      data-testid="login-drawer"
    >
      <div
        className="w-full max-w-md bg-card border border-border rounded-sm shadow-2xl p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6">
          <div
            className="h-10 w-12 flex items-center justify-center text-white font-heading font-bold text-sm mb-4"
            style={{ background: '#002677' }}
          >
            BIL
          </div>
          {mode === 'signin' && (
            <>
              <h2 className="font-heading text-2xl font-semibold tracking-tight">Sign in</h2>
              <p className="text-sm text-muted-foreground mt-1">Welcome back to BIL Business AI.</p>
            </>
          )}
          {mode === 'forgot' && (
            <>
              <button
                onClick={() => { setMode('signin'); setError(''); }}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
                data-testid="forgot-back"
              >
                <ArrowLeft size={12} /> Back to sign in
              </button>
              <h2 className="font-heading text-2xl font-semibold tracking-tight">Forgot password?</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Enter your email. If dev mode is on we&apos;ll show you a reset link immediately;
                otherwise an admin will review the request and share a one-time link with you.
              </p>
            </>
          )}
          {mode === 'forgot-success' && (
            <>
              <h2 className="font-heading text-2xl font-semibold tracking-tight">Request submitted</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {forgotLink
                  ? 'Your reset link is ready below.'
                  : 'An admin will review your request and contact you with a one-time reset link.'}
              </p>
            </>
          )}
        </div>

        {error && (
          <div className="p-3 mb-4 text-sm bg-destructive/10 text-destructive border border-destructive/20 rounded-sm" data-testid="login-error">
            {error}
          </div>
        )}

        {mode === 'signin' && (
          <SignInForm
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            loading={loading}
            onSubmit={handleSubmit}
            onForgotClick={() => { setMode('forgot'); setError(''); setForgotEmail(email); }}
            onGoogleClick={loginWithGoogle}
          />
        )}

        {mode === 'forgot' && (
          <ForgotPasswordForm
            email={forgotEmail}
            setEmail={setForgotEmail}
            loading={forgotLoading}
            onSubmit={handleForgotSubmit}
          />
        )}

        {mode === 'forgot-success' && (
          <ForgotPasswordSuccess
            resetLink={forgotLink}
            onBack={() => { setMode('signin'); setError(''); }}
          />
        )}
      </div>
    </div>
  );
}

function SignInForm({ email, setEmail, password, setPassword, loading, onSubmit, onForgotClick, onGoogleClick }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
          Email
        </Label>
        <div className="relative">
          <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10 rounded-sm"
            placeholder="you@company.com"
            required
            data-testid="login-email"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
            Password
          </Label>
          <button
            type="button"
            onClick={onForgotClick}
            className="text-xs text-primary hover:underline font-medium"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10 rounded-sm"
            placeholder="Enter your password"
            required
            data-testid="login-password"
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full rounded-sm font-medium h-11"
        style={{ background: '#002677' }}
        disabled={loading}
        data-testid="login-submit"
      >
        {loading ? 'Signing in...' : 'Sign in'}
        <ArrowRight className="ml-2" size={18} />
      </Button>

      <div className="relative my-2">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full rounded-sm h-11"
        onClick={onGoogleClick}
        data-testid="google-login"
      >
        <GoogleLogo className="mr-2" size={18} weight="bold" />
        Google
      </Button>

      <p className="text-center text-sm text-muted-foreground pt-2">
        Don&apos;t have an account?{' '}
        <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
          Create account
        </Link>
      </p>
    </form>
  );
}

function ForgotPasswordForm({ email, setEmail, loading, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="forgot-email" className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
          Email
        </Label>
        <div className="relative">
          <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10 rounded-sm"
            placeholder="you@company.com"
            required
            data-testid="forgot-email"
          />
        </div>
      </div>
      <Button
        type="submit"
        className="w-full rounded-sm font-medium h-11"
        style={{ background: '#002677' }}
        disabled={loading}
        data-testid="forgot-submit"
      >
        {loading ? 'Submitting...' : 'Submit request'}
        <ArrowRight className="ml-2" size={18} />
      </Button>
    </form>
  );
}

function ForgotPasswordSuccess({ resetLink, onBack }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-success/10 border border-success/20 rounded-sm flex items-start gap-2">
        <CheckCircle size={20} weight="fill" className="text-success flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Request received</p>
          <p className="text-muted-foreground mt-1">
            {resetLink
              ? 'Copy the one-time link below and open it in a new tab to set a new password. It expires in 1 hour.'
              : 'An admin has been notified. Once approved you will receive a one-time reset link.'}
          </p>
        </div>
      </div>

      {resetLink && (
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
            One-time reset link (dev mode)
          </Label>
          <div className="flex gap-2">
            <Input
              readOnly
              value={resetLink}
              className="rounded-sm font-mono text-xs"
              onFocus={(e) => e.target.select()}
              data-testid="reset-link-field"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => {
                navigator.clipboard.writeText(resetLink);
                toast.success('Reset link copied');
              }}
              data-testid="copy-reset-link"
            >
              <Copy size={14} />
            </Button>
          </div>
          <Button
            type="button"
            className="w-full rounded-sm"
            style={{ background: '#002677' }}
            onClick={() => { window.location.href = resetLink; }}
            data-testid="open-reset-link"
          >
            Open reset page now
            <ArrowRight className="ml-2" size={16} />
          </Button>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full rounded-sm"
        onClick={onBack}
        data-testid="back-to-signin"
      >
        <ArrowLeft className="mr-2" size={14} /> Back to sign in
      </Button>
    </div>
  );
}
